import { Card, Button } from "@/components/shared";

const TIER_NAMES: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  professional: "Professional",
};

const NEXT_TIER: Record<string, { tier: string; price: string; leads: number; regions: number }> = {
  free: { tier: "starter", price: "R99/mo", leads: 10, regions: 1 },
  starter: { tier: "professional", price: "R199/mo", leads: 30, regions: 3 },
};

interface UpgradePromptProps {
  currentTier: string;
  leadLimit: number;
  onUpgrade?: () => void;
  variant?: "modal" | "banner";
}

export function UpgradePrompt({ currentTier, leadLimit, onUpgrade, variant = "banner" }: UpgradePromptProps) {
  const next = NEXT_TIER[currentTier];
  if (!next) return null; // Already on highest tier

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      window.location.href = "/agent/subscription";
    }
  };

  if (variant === "banner") {
    return (
      <div className="bg-amber-900/30 border border-amber-500/30 rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-amber-200">You've reached your plan limit</p>
            <p className="text-sm text-amber-200/70">
              Your {TIER_NAMES[currentTier]} plan allows {leadLimit} leads/month.
              Upgrade to {TIER_NAMES[next.tier]} for {next.leads} leads and {next.regions} region{next.regions > 1 ? "s" : ""}.
            </p>
          </div>
          <Button variant="primary" onClick={handleUpgrade} className="shrink-0">
            Upgrade — {next.price}
          </Button>
        </div>
      </div>
    );
  }

  // Modal variant
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-500/20 flex items-center justify-center">
            <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Lead Limit Reached</h3>
          <p className="text-white/70 mb-4">
            Your <span className="font-semibold capitalize">{TIER_NAMES[currentTier]}</span> plan
            allows {leadLimit} leads per month. Upgrade to unlock more.
          </p>

          <div className="bg-white/5 rounded-lg p-3 mb-4 text-left">
            <p className="font-semibold text-sun mb-1">{TIER_NAMES[next.tier]} Plan — {next.price}</p>
            <ul className="text-sm text-white/60 space-y-1">
              <li>• {next.leads} leads per month</li>
              <li>• {next.regions} region{next.regions > 1 ? "s" : ""}</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button variant="primary" onClick={handleUpgrade} className="flex-1">
              Upgrade Now
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
